import { Button, DatePicker, Drawer, Input, Select, Table, Tag } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import deepEqual from "deep-equal"
import moment from "moment"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import ReactJson from "react-json-view"
import styled from "styled-components"

import { getLogPaths, getLogs } from "../../../../../api"
import StyledScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"

dayjs.extend(relativeTime)

// ----------------------------------------------------------------------------

function _Logs(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [search, setSearch] = useState({
        level: [],
        path: [],
        message: "",
        before: null,
        after: null,
        sort: "-timestamp",
        limit: 20,
    })

    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })

    const [paths, setPaths] = useState([])
    const [logs, setLogs] = useState([])
    const levels = ["debug", "info", "warning", "error", "critical"]
    const prevRef = useRef(null)

    const [selected, setSelected] = useState(null)

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        getLogPaths(token).then((res) => setPaths(res.paths))
        //eslint-disable-next-line
    }, [token])

    useEffect(() => {
        let isCurrent = true
        let interval
        const exec = () =>
            getLogs(
                {
                    ...search,
                    path: search.path.length > 0 ? search.path.join(",") : null,
                    level:
                        search.level.length > 0 ? search.level.join(",") : null,
                },
                pagination.current,
                token
            )
                .then((response) => {
                    const { rows, count } = response

                    if (isCurrent && !deepEqual(prevRef.current, rows)) {
                        prevRef.current = rows
                        setLogs(rows)
                        setPagination({ ...pagination, total: count })
                    }
                })
                .catch((err) => {
                    //
                })
        const timeout = setTimeout(() => {
            exec()
            interval = setInterval(() => {
                exec()
            }, 2000)
        }, 500)

        return () => {
            isCurrent = false
            clearTimeout(timeout)
            interval && clearInterval(interval)
        }
        //eslint-disable-next-line
    }, [token, search, pagination.current])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function updateSearch(field, isDate) {
        return (value) => {
            if (value?.target?.value) {
                setSearch((p) => ({ ...p, [field]: value.target.value }))
            } else if (isDate) {
                setSearch((p) => ({
                    ...p,
                    [field]: value ? moment(value).toISOString() : null,
                }))
            } else {
                setSearch((p) => ({ ...p, [field]: value }))
            }
            setPagination((prev) => ({ ...prev, current: 1 }))
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <StyledScreenTitle text="Developer"></StyledScreenTitle>
            <div className="top-bar">
                <Select
                    mode="multiple"
                    placeholder="Livello"
                    value={search.level}
                    options={levels.map((l) => ({ label: l, value: l }))}
                    onChange={updateSearch("level")}
                    allowClear
                ></Select>
                <Select
                    mode="multiple"
                    placeholder="Percorso"
                    value={search.path}
                    options={paths.map((l) => ({ label: l, value: l }))}
                    onChange={updateSearch("path")}
                    filterOption={(search, current) => {
                        return new RegExp(search, "igm").test(current.label)
                    }}
                    showSearch
                    allowClear
                ></Select>
                <Input
                    placeholder="Messaggio"
                    value={search.message}
                    onChange={updateSearch("message")}
                />
                <DatePicker
                    placeholder="Inizio"
                    value={search.after ? moment(search.after) : null}
                    onChange={updateSearch("after", true)}
                    showTime
                    format={"DD/MM/YY HH:mm"}
                />
                <DatePicker
                    placeholder="Fine"
                    value={search.before ? moment(search.before) : null}
                    onChange={updateSearch("before", true)}
                    showTime
                    format={"DD/MM/YY HH:mm"}
                />
            </div>
            <Table
                dataSource={logs}
                pagination={{ ...pagination, showSizeChanger: false }}
                onChange={(p) => {
                    setPagination({ ...pagination, current: p.current })
                }}
                columns={getColumns()}
                onRow={(record) => {
                    return {
                        onClick: () => setSelected(record),
                    }
                }}
            />
            <Drawer
                width={"60vw"}
                visible={!!selected}
                title={`${selected?.path} - ${dayjs(selected?.timestamp).format(
                    "DD/MM/YY HH:mm"
                )}`}
                onClose={() => setSelected(null)}
            >
                <ReactJson src={selected?.data || {}} />
            </Drawer>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_Logs.propTypes = {
    className: PropTypes.string.isRequired,
}

// ----------------------------------------------------------------------------

const Logs = styled(_Logs)`
    & {
        h1 {
            text-align: left;
        }

        .top-bar {
            margin: 20px 0;

            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 12px;

            > * {
                width: 150px;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default Logs

const colorMap = {
    debug: "#00bfff",
    info: "#0099ff",
    warning: "#ffb100",
    error: "#ff2000",
    critical: "#901200",
}

function getColumns() {
    return [
        {
            key: "path",
            title: "Percorso",
            dataIndex: "path",
        },
        {
            key: "level",
            title: "Livello",
            dataIndex: "level",
            render: (level) => <Tag color={colorMap[level]}>{level}</Tag>,
        },
        {
            key: "timestamp",
            title: "Data",
            dataIndex: "timestamp",
            render: (ts) => dayjs(ts).fromNow(),
        },
        {
            key: "message",
            title: "Messaggio",
            dataIndex: "message",
        },
        {
            key: "data",
            title: "Dettaglio",
            render: () => <Button>Apri</Button>,
        },
    ]
}
